<template>
  <div class="voyage-binding">
    <!-- 固定头部区域 -->
    <div class="fixed-header">
      <!-- 搜索栏 -->
      <div class="search-section">
        <van-search
          v-model="searchKeyword"
          placeholder="搜索船舶名称、航次号或MMSI"
          @search="onSearch"
          @clear="onClear"
          show-action
        >
          <template #action>
            <div @click="onSearch">搜索</div>
          </template>
        </van-search>
      </div>

      <!-- 操作栏 -->
      <div class="action-section">
        <div class="selected-info">
          已选择 {{ selectedShips.length }} 艘船舶
        </div>
        <van-button
          type="primary"
          size="small"
          :disabled="selectedShips.length === 0"
          @click="onBindDeviceClick"
        >
          绑定设备
        </van-button>
      </div>
    </div>

    <!-- 可滚动的船舶列表区域 -->
    <div class="scrollable-content">
      <van-pull-refresh
        v-model="refreshing"
        class="page-pull-refresh page-pull-refresh--fill"
        @refresh="onRefresh"
      >
        <div class="ship-list">
          <van-loading v-if="loading" type="spinner" color="#667eea" class="loading-center">
            加载中...
          </van-loading>
          <van-card
            v-for="ship in filteredShips"
            :key="ship.id"
            class="ship-card"
            :class="{ 'selected': isSelected(ship.id) }"
            @click="onShipSelect(ship)"
          >
            <template #title>
              <div class="ship-header">
                <div class="ship-info">
                  <h3 class="ship-name">{{ ship.shipName }}</h3>
                  <van-tag class="ship-type-tag" type="primary" size="small">{{ ship.shipType }}</van-tag>
                </div>
                <van-checkbox
                  :value="isSelected(ship.id)"
                  @click.stop="onShipSelect(ship)"
                  class="ship-checkbox"
                />
              </div>
            </template>
            <template #desc>
              <div class="ship-details">
                <div class="detail-row">
                  <span class="label">码头：</span>
                  <span class="value">{{ getPortName(ship.portCode) }}</span>
                </div>
                <div class="detail-row">
                  <span class="label">航次号：</span>
                  <span class="value">{{ ship.hch }}</span>
                </div>
                <div class="detail-row">
                  <span class="label">MMSI：</span>
                  <span class="value">{{ ship.mmsi }}</span>
                </div>
                <div class="detail-row">
                  <span class="label">绑定状态：</span>
                  <van-tag :type="ship.bindingState === 1 ? 'success' : 'warning'" size="small">
                    {{ ship.bindingState === 1 ? '已绑定' : '未绑定' }}
                  </van-tag>
                </div>
              </div>
            </template>
            <template #footer>
              <div class="ship-footer">
                <div class="binding-info">
                  <span v-if="ship.bindingState === 1" class="bound-device">
                    已绑定设备：{{ getBindingDeviceName(ship) }}
                    <van-button
                      type="warning"
                      size="small"
                      class="unbind-btn"
                      @click.stop="onUnbindDevice(ship)"
                    >
                      解除绑定
                    </van-button>
                  </span>
                  <span v-else class="no-binding">未绑定设备</span>
                </div>
              </div>
            </template>
          </van-card>
        </div>

        <van-empty
          v-if="filteredShips.length === 0"
          description="暂无船舶数据"
        >
          <template #image>
            <svg width="120" height="80" viewBox="0 0 120 80" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="60" cy="40" r="20" fill="none" stroke="#d9d9d9" stroke-width="3"/>
              <path d="M75 55 L85 65" stroke="#d9d9d9" stroke-width="3" stroke-linecap="round"/>
              <circle cx="60" cy="40" r="12" fill="none" stroke="#d9d9d9" stroke-width="1.5" stroke-dasharray="3 3"/>
            </svg>
          </template>
        </van-empty>
      </van-pull-refresh>
    </div>

    <!-- 设备绑定弹窗 -->
    <van-popup
      v-model="showDeviceList"
      position="bottom"
      :style="{ height: '70%' }"
      round
    >
      <div class="popup-header">
        <h3>选择绑定设备</h3>
        <van-icon name="cross" @click="onCloseDeviceList" />
      </div>
      
      <div class="device-list">
        <div class="selected-ships-info">
          <h4>待绑定船舶（{{ selectedShips.length }}艘）</h4>
          <div class="selected-ships">
            <van-tag
              v-for="ship in selectedShips"
              :key="ship.id"
              type="primary"
              size="small"
              class="ship-tag"
            >
              {{ ship.shipName }}
            </van-tag>
          </div>
        </div>

        <div class="device-selection">
          <h4>可用设备列表</h4>
          <van-loading v-if="devicesLoading" type="spinner" color="#667eea" class="loading-center">
            加载设备中...
          </van-loading>
          <van-radio-group v-model="selectedDevice" v-else>
            <van-cell-group>
              <van-cell
                v-for="device in availableDevices"
                :key="device.id"
                :title="device.deviceName"
                :label="`设备编号: ${device.deviceSN}`"
                clickable
                @click="selectedDevice = device.id"
              >
                <template #icon>
                  <van-icon name="cluster-o" class="device-icon" />
                </template>
                <template #right-icon>
                  <van-radio :name="device.id" />
                </template>
                <template #extra>
                  <van-tag :type="device.isOnline ? 'success' : 'warning'" size="small">
                    {{ device.isOnline ? '在线' : '离线' }}
                  </van-tag>
                </template>
              </van-cell>
            </van-cell-group>
          </van-radio-group>
          <van-empty
            v-if="!devicesLoading && availableDevices.length === 0"
            description="暂无可用设备"
          >
            <template #image>
              <svg width="120" height="80" viewBox="0 0 120 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="60" cy="40" r="20" fill="none" stroke="#d9d9d9" stroke-width="3"/>
                <path d="M75 55 L85 65" stroke="#d9d9d9" stroke-width="3" stroke-linecap="round"/>
                <circle cx="60" cy="40" r="12" fill="none" stroke="#d9d9d9" stroke-width="1.5" stroke-dasharray="3 3"/>
              </svg>
            </template>
          </van-empty>
        </div>

        <div class="binding-actions">
          <van-button
            type="primary"
            block
            round
            :disabled="!selectedDevice"
            :loading="bindingLoading"
            @click="confirmBinding"
          >
            确认绑定
          </van-button>
        </div>
      </div>
    </van-popup>

    <!-- 解除绑定确认弹窗 -->
    <van-popup
      v-model="showUnbindConfirm"
      :style="{ width: '85%', borderRadius: '16px' }"
      round
      :close-on-click-overlay="false"
    >
      <div class="custom-confirm-dialog">
        <div class="confirm-icon">
          <van-icon name="warning-o" color="#ff976a" size="48" />
        </div>
        <div class="confirm-title">
          解除绑定
        </div>
        <div class="confirm-message">
          是否需要解除
          <span style="color:#ee0a24;font-weight:bold;">{{ unbindShip && unbindShip.shipName }}</span>
          的绑定？
        </div>
        <div class="confirm-actions">
          <van-button
            round
            size="small"
            plain
            @click="showUnbindConfirm = false"
          >
            取消
          </van-button>
          <van-button
            round
            size="small"
            type="warning"
            @click="confirmUnbind"
          >
            解除绑定
          </van-button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import axios from '@/utils/request'
import { fetchMultiDic, default as globalDic } from '@/utils/dic'

export default {
  name: 'VoyageBinding',
  data() {
    return {
      searchKeyword: '',
      refreshing: false,
      selectedShips: [],
      selectedDevice: '',
      showDeviceList: false,
      bindingLoading: false,
      loading: false,
      ships: [],
      availableDevices: [],
      devicesLoading: false,
      showUnbindConfirm: false,
      unbindShip: null,
    };
  },
  
  computed: {
    filteredShips() {
      let filtered = this.ships;
      
      // 如果有搜索关键词，进行过滤
      if (this.searchKeyword) {
        filtered = filtered.filter(ship => 
          ship.shipName.includes(this.searchKeyword) || 
          ship.hch.includes(this.searchKeyword) ||
          ship.mmsi.includes(this.searchKeyword)
        );
      }
      
      // 排序：未绑定的在上方，已绑定的在下方
      return filtered.sort((a, b) => {
        // bindingState: 0=未绑定, 1=已绑定
        // 未绑定的排在前面（升序）
        return a.bindingState - b.bindingState;
      });
    }
  },
  
  methods: {
    lockPageScroll() {
      this.__prevHtmlOverflow = document.documentElement.style.overflow;
      this.__prevBodyOverflow = document.body.style.overflow;
      this.__scrollParent = this.$el && this.$el.parentElement ? this.$el.parentElement : null;
      this.__prevParentOverflowY = this.__scrollParent ? this.__scrollParent.style.overflowY : '';
      this.__prevParentTouchAction = this.__scrollParent ? this.__scrollParent.style.touchAction : '';
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      if (this.__scrollParent) {
        this.__scrollParent.style.overflowY = 'hidden';
        this.__scrollParent.style.touchAction = 'none';
      }
    },

    unlockPageScroll() {
      document.documentElement.style.overflow = this.__prevHtmlOverflow || '';
      document.body.style.overflow = this.__prevBodyOverflow || '';
      if (this.__scrollParent) {
        this.__scrollParent.style.overflowY = this.__prevParentOverflowY || '';
        this.__scrollParent.style.touchAction = this.__prevParentTouchAction || '';
      }
    },

    async fetchShips() {
      this.loading = true;
      try {
        const params = {
          portCode: "",
          hch: "",
          shipName: "",
          shipType: "",
          mmsi: "",
          bindingState: -99
        };
        
        // 如果有搜索关键词，添加到查询参数中
        if (this.searchKeyword) {
          if (this.searchKeyword.includes('HC')) {
            params.hch = this.searchKeyword;
          } else if (this.searchKeyword.startsWith('M') || this.searchKeyword.startsWith('F')) {
            params.mmsi = this.searchKeyword;
          } else {
            params.shipName = this.searchKeyword;
          }
        }
        
        const res = await axios.post('/api/Ship/Query', params);
        if (res && res.data && res.data.success) {
          this.ships = res.data.data || [];
        } else {
          this.$toast.fail(res.data.message || '查询船舶数据失败');
          this.ships = [];
        }
      } catch (err) {
        console.error('查询船舶数据失败:', err);
        this.$toast.fail('查询船舶数据失败，请检查网络连接');
        this.ships = [];
      } finally {
        this.loading = false;
      }
    },
    
    getPortName(portCode) {
      if (!portCode) {
        return '';
      }
      
      if (!globalDic.D_Port || globalDic.D_Port.length === 0) {
        return portCode;
      }
      
      const port = globalDic.D_Port.find(item => item.code === portCode);
      
      if (port && port.description) {
        return port.description;
      } else {
        return portCode;
      }
    },
    
    getBindingDeviceName(ship) {
      // 如果bindingTkName存在且不为空，则显示设备名称
      if (ship.bindingTkName && ship.bindingTkName.trim() !== '') {
        return ship.bindingTkName;
      }
      // 如果bindingTkName为空或null，则显示"设备id:tkId"
      return `设备id:${ship.bindingTKId}`;
    },
    
    async fetchAvailableDevices() {
      this.devicesLoading = true;
      try {
        // 获取选中船舶的码头代码
        const portCodes = [...new Set(this.selectedShips.map(ship => ship.portCode))];
        
        if (portCodes.length === 0) {
          this.$toast.fail('请先选择船舶');
          this.devicesLoading = false;
          return;
        }
        
        // 如果选中的船舶来自不同码头，提示用户
        if (portCodes.length > 1) {
          this.$toast.fail('只能绑定同一码头的船舶，请重新选择');
          this.devicesLoading = false;
          return;
        }
        
        const portCode = portCodes[0];
        
        // 查询设备列表
        const res = await axios.post('/api/Device/Query', {
          portCode: portCode,
          state: 0 // 只查询未绑定的设备
        });
        
        if (res && res.data && res.data.success) {
          // 过滤出未绑定的设备
          this.availableDevices = (res.data.data || []).filter(device => 
            device.portCode === portCode
          );
          
          if (this.availableDevices.length === 0) {
            this.$toast.fail('该码头暂无可用设备');
          }
        } else {
          this.$toast.fail(res.data.message || '查询设备列表失败');
          this.availableDevices = [];
        }
      } catch (err) {
        console.error('查询设备列表失败:', err);
        this.$toast.fail('查询设备列表失败，请检查网络连接');
        this.availableDevices = [];
      } finally {
        this.devicesLoading = false;
      }
    },
    
    isSelected(shipId) {
      return this.selectedShips.some(ship => ship.id === shipId);
    },
    
    onShipSelect(ship) {
      const index = this.selectedShips.findIndex(s => s.id === ship.id);
      if (index > -1) {
        this.selectedShips.splice(index, 1);
      } else {
        this.selectedShips.push(ship);
      }
    },
    
    onSearch() {
      this.fetchShips();
    },
    
    onClear() {
      this.searchKeyword = '';
      this.fetchShips();
    },
    async onRefresh() {
      try {
        this.selectedShips = [];
        await this.fetchShips();
      } finally {
        this.refreshing = false;
      }
    },
    
    async onBindDeviceClick() {
      if (this.selectedShips.length === 0) {
        this.$toast.fail('请先选择要绑定的船舶');
        return;
      }
      
      // 检查是否有已绑定的船舶
      const boundShips = this.selectedShips.filter(ship => ship.bindingState === 1);
      if (boundShips.length > 0) {
        const shipNames = boundShips.map(ship => ship.shipName).join('、');
        this.$toast.fail(`${shipNames}已经绑定设备，请检查并重新选择`);
        return;
      }
      
      // 查询可用设备
      await this.fetchAvailableDevices();
      
      // 如果有可用设备，显示设备选择弹窗
      if (this.availableDevices.length > 0) {
        this.showDeviceList = true;
      }
    },
    

    
    onCloseDeviceList() {
      this.showDeviceList = false;
      this.selectedDevice = '';
      this.availableDevices = [];
    },
    
    async confirmBinding() {
      if (!this.selectedDevice) {
        this.$toast.fail('请选择要绑定的设备');
        return;
      }
      
      this.bindingLoading = true;
      
      try {
        // 准备绑定参数
        const bindingParams = {
          ids: this.selectedShips.map(ship => ship.id),
          tkId: this.selectedDevice,
          bindingState: 1
        };
        
        // 调用绑定接口
        const res = await axios.post('/api/Ship/BindingDevice', bindingParams);
        
        if (res && res.data && res.data.success) {
          this.$toast.success(`成功绑定 ${this.selectedShips.length} 艘船舶到设备`);
          
          // 重新获取船舶数据以更新绑定状态
          await this.fetchShips();
          
          // 重置状态
          this.selectedShips = [];
          this.selectedDevice = '';
          this.showDeviceList = false;
          this.availableDevices = [];
        } else {
          this.$toast.fail(res.data.message || '绑定失败');
        }
      } catch (err) {
        console.error('绑定设备失败:', err);
        this.$toast.fail('绑定设备失败，请检查网络连接');
      } finally {
        this.bindingLoading = false;
      }
    },

    onUnbindDevice(ship) {
      this.unbindShip = ship;
      this.showUnbindConfirm = true;
    },
    async confirmUnbind() {
      if (!this.unbindShip) return;
      try {
        this.$toast.loading({ message: '正在解除绑定...', forbidClick: true });
        const res = await axios.post('/api/Ship/UnBindingDevice', { id: this.unbindShip.id });
        if (res && res.data && res.data.success) {
          this.$toast.success('解除绑定成功');
          await this.fetchShips();
        } else {
          this.$toast.fail(res.data.message || '解除绑定失败');
        }
      } catch (err) {
        this.$toast.fail('解除绑定失败，请检查网络：' + err);
      } finally {
        this.showUnbindConfirm = false;
        this.unbindShip = null;
      }
    },
  },
  
  async mounted() {
    this.lockPageScroll();
    // 获取字典数据
    await fetchMultiDic(['D_Port']);
    // 获取船舶数据
    await this.fetchShips();
  },

  beforeDestroy() {
    this.unlockPageScroll();
  },

  beforeRouteLeave(to, from, next) {
    this.unlockPageScroll();
    next();
  }
};
</script>

<style scoped>
html, body {
  height: 100%;
  overflow: hidden;
}

.voyage-binding {
  background: transparent;
  min-height: 100vh;
  height: 100vh;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 0 0 14px;
  overflow: hidden;
}

.fixed-header {
  position: sticky;
  top: 0;
  z-index: 2;
  background: transparent;
  height: auto;
  box-shadow: none;
  padding: 0;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.search-section {
  background: rgba(255, 255, 255, 0.92);
  padding: 10px;
  border: 1px solid rgba(215, 229, 251, 0.96);
  border-radius: 24px;
  box-shadow: var(--shadow-card);
}

.action-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(215, 229, 251, 0.96);
  border-radius: 24px;
  box-shadow: var(--shadow-card);
}

.scrollable-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  background: transparent;
  padding-bottom: 96px;
}

.page-pull-refresh--fill {
  min-height: 100%;
}

.page-pull-refresh--fill :deep(.van-pull-refresh__track) {
  min-height: 100%;
}

.selected-info {
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
}

.ship-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 16px;
}

.loading-center {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 0;
}

.ship-card {
  border-radius: 24px;
  overflow: hidden;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(215, 229, 251, 0.96);
  box-shadow: var(--shadow-card);
}

.ship-card.selected {
  border-color: var(--brand-500);
  box-shadow: 0 14px 28px rgba(58, 122, 254, 0.18);
}

.ship-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.ship-info {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.ship-name {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: var(--text-primary);
  max-width: 70%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.ship-type-tag {
  flex-shrink: 0;
}

.ship-checkbox {
  margin-left: 12px;
  padding: 6px;
  flex-shrink: 0;
}

.ship-checkbox :deep(.van-checkbox__icon) {
  font-size: 18px;
}

.ship-details {
  padding: 12px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-row {
  display: flex;
  align-items: center;
  margin-bottom: 0;
}

.detail-row:last-child {
  margin-bottom: 0;
}

.label {
  color: var(--text-secondary);
  font-size: 14px;
  width: 80px;
  flex-shrink: 0;
}

.value {
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  flex: 1;
}

.ship-footer {
  padding-top: 8px;
  border-top: 1px solid #ebedf0;
}

.binding-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 24px;
}

.bound-device {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  color: #07c160;
  font-weight: 500;
  font-size: 12px;
  line-height: 1.2;
}

.no-binding {
  color: #ee0a24;
  font-size: 12px;
  line-height: 1.2;
}

.no-binding {
  color: #ee0a24;
}

.unbind-btn {
  margin-left: 12px;
}

/* 紧凑化 van-card 默认内边距 */
:deep(.van-card__content) {
  padding: 14px 16px;
}

:deep(.van-card__footer) {
  padding: 0 16px 14px;
}

/* 收紧底部小按钮高度 */
:deep(.van-card__footer .van-button--small) {
  height: 22px;
  line-height: 20px;
  padding: 0 10px;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #ebedf0;
}

.popup-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
}

.device-list {
  padding: 16px;
  height: calc(100% - 60px);
  overflow-y: auto;
}

.selected-ships-info {
  margin-bottom: 24px;
}

.selected-ships-info h4 {
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
}

.selected-ships {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.ship-tag {
  margin: 0 4px 4px 0;
  border-radius: 12px;
  padding: 4px 8px;
  font-size: 12px;
}

.selected-ships {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 8px;
}

.device-selection {
  margin-bottom: 24px;
  position: relative;
  min-height: 200px;
}

.device-selection h4 {
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
}

.device-selection .loading-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.device-icon {
  color: #667eea;
  font-size: 20px;
}

.binding-actions {
  position: sticky;
  bottom: 0;
  background: white;
  padding-top: 16px;
}

.van-search {
  margin-bottom: 0;
  min-height: 6px;
  --van-search-padding: 0 0px;
  --van-search-background: #fff;
}

.search-section :deep(.van-search__content) {
  min-height: 34px;
  padding: 0 10px;
}

.van-search .van-field__control {
  font-size: 14px;
  min-height: 6px;
  padding: 0;
}

@media (max-width: 767px) {
  .voyage-binding {
    gap: 12px;
    padding-bottom: 12px;
  }

  .fixed-header {
    gap: 12px;
  }

  .search-section,
  .action-section {
    border-radius: 20px;
  }

  .search-section {
    padding: 8px 12px;
  }

  .action-section {
    gap: 10px;
  }

  .ship-list {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .ship-header {
    align-items: center;
  }

  .ship-name {
    max-width: none;
    flex: 1;
  }
}

/* 深色模式适配 */
@media (prefers-color-scheme: dark) {
  .voyage-binding {
    background-color: #1a1a1a;
  }
  
  .search-section,
  .action-section {
    background: #2a2a2a;
    border-bottom-color: #444;
  }
  
  .ship-name {
    color: white;
  }
  
  .value {
    color: #ccc;
  }
  
  .ship-card {
    background: #333;
  }
  
  .ship-footer {
    border-top-color: #444;
  }
  
  .popup-header {
    background: #2a2a2a;
    border-bottom-color: #444;
  }
  
  .popup-header h3,
  .selected-ships-info h4,
  .device-selection h4 {
    color: white;
  }
  
  .device-list {
    background: #2a2a2a;
  }
  
  .binding-actions {
    background: #2a2a2a;
  }
}

/* 若未引入custom-confirm-dialog等样式，请复制UserManagement.vue相关样式到本文件style中 */
</style>
